import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, CalendarDays, TrendingUp, Upload, Tag, Users, LogOut, Menu, X, PiggyBank, FolderKanban, History,
} from "lucide-react";
import { KakeboLogo } from "@/components/KakeboLogo";
import { useState } from "react";

const NAV = [
  { to: "/", label: "Tableau de Bord", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
  { to: "/mensuel", label: "Dépenses Mensuelles", icon: CalendarDays, testid: "nav-monthly-link" },
  { to: "/epargne", label: "Épargne", icon: PiggyBank, testid: "nav-savings-link" },
  { to: "/annuel", label: "Prévisions exceptionnelles", icon: TrendingUp, testid: "nav-annual-link" },
  { to: "/projets", label: "Projets", icon: FolderKanban, testid: "nav-projects-link" },
  { to: "/import", label: "Import PDF & Excel", icon: Upload, testid: "nav-import-link" },
  { to: "/prestataires", label: "Prestataires & Catégories", icon: Tag, testid: "nav-vendors-link" },
  { to: "/membres", label: "Info Famille", icon: Users, testid: "nav-members-link" },
  { to: "/historique", label: "Historique", icon: History, testid: "nav-history-link" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-5 h-16 border-b border-border">
        <KakeboLogo size={36} />
        <div className="min-w-0">
          <div className="font-display font-bold text-[17px] leading-tight truncate">Kakebo</div>
          <div className="text-[11px] text-muted-foreground truncate">Suivi du foyer</div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px] shrink-0" />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2 mb-1">
          <div className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center font-semibold text-sm">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground" onClick={handleLogout} data-testid="logout-button">
          <LogOut className="w-[18px] h-[18px]" /> Déconnexion
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card fixed inset-y-0 z-30">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 flex flex-col border-r border-border bg-card">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 h-14 flex items-center justify-between px-4 border-b border-border bg-card/80 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <KakeboLogo size={30} />
            <span className="font-display font-bold text-lg">Kakebo</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
